import {
  AUTH_PERMISSION_PROFILE_NAMES,
  AUTH_SCOPES,
  DEFAULT_AUTH_PROFILE,
  type AuthPermissionProfile,
  type AuthProfileSelection,
  type AuthScope,
} from "./test-user";

/**
 * Well-known localStorage key holding the per-test auth profile override as a
 * JSON object (e.g. `{ "profile": "no-connectlogo", "scope": "operator" }`).
 * Playwright sets it per test via `addInitScript` so a spec can select the
 * seeded user's permission set and company/operator scope without a second
 * seeded user or a rebuild (design D5, adr/0003 env-gated mock auth). It mirrors
 * the flag-override seam ({@link ../mocks/flag-overrides}); like that seam it is
 * read only on the mock-auth boot path ({@link resolveMockAuthProfile}), so it
 * is inert in a real deployment (which never calls `setupMockAuth`).
 */
const AUTH_OVERRIDE_STORAGE_KEY = "e2e:auth-override";

function isAuthPermissionProfile(
  value: unknown
): value is AuthPermissionProfile {
  return (
    typeof value === "string" &&
    (AUTH_PERMISSION_PROFILE_NAMES as string[]).includes(value)
  );
}

function isAuthScope(value: unknown): value is AuthScope {
  return (
    typeof value === "string" &&
    (AUTH_SCOPES as readonly string[]).includes(value)
  );
}

interface ResolveMockAuthProfileArgs {
  storage: Pick<Storage, "getItem">;
}

/**
 * Resolve the auth profile the mock session boots as: parse the override from
 * storage and fall back per field to {@link DEFAULT_AUTH_PROFILE}. A missing
 * key, malformed JSON, or an unrecognized profile/scope value can never crash
 * boot — each falls back to the default, so an absent override reproduces
 * today's full-access, company-scoped user exactly.
 */
function resolveMockAuthProfile({
  storage,
}: ResolveMockAuthProfileArgs): AuthProfileSelection {
  const raw = storage.getItem(AUTH_OVERRIDE_STORAGE_KEY);
  if (!raw) return DEFAULT_AUTH_PROFILE;

  try {
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return DEFAULT_AUTH_PROFILE;

    const { profile, scope } = parsed as Record<string, unknown>;
    return {
      profile: isAuthPermissionProfile(profile)
        ? profile
        : DEFAULT_AUTH_PROFILE.profile,
      scope: isAuthScope(scope) ? scope : DEFAULT_AUTH_PROFILE.scope,
    };
  } catch {
    return DEFAULT_AUTH_PROFILE;
  }
}

export { AUTH_OVERRIDE_STORAGE_KEY, resolveMockAuthProfile };
